import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaCloud, FaClipboard, FaCheck } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const FindingsList = ({ findingsData, findingCategory }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedResourceName, setCopiedResourceName] = useState(null);
  const [severityFilter, setSeverityFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const itemsPerPage = 10;

  // Reset pagination to the first page whenever findingsData or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [findingsData, severityFilter, regionFilter, serviceFilter, accountFilter]);
  // Filter data based on selected filters
  const filteredData = findingsData.filter((finding) => {
    const matchesSeverity = !severityFilter || finding.severity?.S === severityFilter;
    const matchesRegion = !regionFilter || finding.region?.S === regionFilter;
    const matchesService = !serviceFilter || finding.service?.S === serviceFilter;
    const matchesAccount = !accountFilter || finding.accountId?.S === accountFilter;
    return matchesSeverity && matchesRegion && matchesService && matchesAccount;
  });

  // Get unique values for filter dropdowns
  const uniqueSeverities = [...new Set(findingsData.map(f => f.severity?.S).filter(Boolean))];
  const uniqueRegions = [...new Set(findingsData.map(f => f.region?.S).filter(Boolean))];
  const uniqueServices = [...new Set(findingsData.map(f => f.service?.S).filter(Boolean))];
  const uniqueAccounts = [...new Set(findingsData.map(f => f.accountId?.S).filter(Boolean))];

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleViewDetails = (finding) => {
    setSelectedFinding(finding);
    setIsModalOpen(true);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedResourceName(text);
      setTimeout(() => setCopiedResourceName(null), 3000); // Reset after 3 seconds
    });
  };

  const severityColors = {
    High: "bg-custom-danger",
    Medium: "bg-custom-warning",
    Low: "bg-custom-secondary",
    Critical: "bg-muted text-light",
  };

  const styles = {
    container: {
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#fff",
      borderRadius: "10px 10px 10px 10px",
    },
    header: {
      backgroundColor: "#6D5BFF",
      color: "#fff",
      padding: "15px",
      fontSize: "1.2rem",
      borderRadius: "10px 10px 0px 0px",
    },
    row: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "15px 12px",
      borderBottom: "1px solid #ddd",
      color: "#333",
    },
    icon: {
      width: "30px",
      height: "30px",
      backgroundColor: "#666bff54",
      borderRadius: "50%",
      color: "#6D5BFF",
      fontSize: "1rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "10px",
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "20px",
      padding: "10px",
    },
    button: {
      padding: "6px 10px",
      border: "none",
      borderRadius: "5px",
      backgroundColor: "#6D5BFF",
      color: "#fff",
      cursor: "pointer",
      fontSize: "1rem",
    },
    disabledButton: {
      padding: "6px 10px",
      border: "none",
      borderRadius: "5px",
      backgroundColor: "#ccc",
      color: "#fff",
      cursor: "not-allowed",
      fontSize: "1rem",
    },
  };

  return (
    <div className="container finding-list p-0 shadow rounded">
      <div style={styles.container}>
        <div style={styles.header} className="text-lg-center">
          Detailed Findings{" "}
          {findingCategory ? ` - ${findingCategory}` : ""}
        </div>

        {/* Filters */}
        <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #ddd" }}>
          <div className="row g-2">
            <div className="col-md-4">
              <select
                className="form-select form-select-sm"
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <option value="">All Severities</option>
                {uniqueSeverities.map((severity) => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-select form-select-sm"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <option value="">All Regions</option>
                {uniqueRegions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-select form-select-sm"
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
              >
                <option value="">All Services</option>
                {uniqueServices.map((service) => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Findings Rows */}
        <div className="list-group">
          {currentData.map((finding, index) => (
            <div className="finding-list" key={index} style={styles.row}>
              <div className="d-flex">
                <div style={styles.icon}>
                  <FaCloud color="#6D5BFF" />
                </div>
                <span
                  style={{
                    flex: 1,
                    wordBreak: "break-all",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                  className="text-muted description"
                >
                  {finding.description?.S || "No description available"}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-sm button fw-bold py-1 mx-2"
                  onClick={() => handleViewDetails(finding)}
                >
                  <small>View</small>
                </button>
                <span
                  className={`badge ${
                    severityColors[finding.severity?.S] || ""
                  }`}
                  style={{ padding: "8px 12px", fontSize: "12px" }}
                >
                  {finding.severity?.S || "Unknown"}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div style={styles.footer}>
          <button
            style={currentPage > 1 ? styles.button : styles.disabledButton}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <small>
            <span className="text-muted fw-bold">
              Page {currentPage} of {totalPages}
            </span>
          </small>
          <button
            style={currentPage < totalPages ? styles.button : styles.disabledButton}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal for Viewing Details */}
      {isModalOpen && selectedFinding && (
        <Modal
          className="findingListModal"
          show={isModalOpen}
          onHide={() => setIsModalOpen(false)}
          centered
          size="xl"
          style={{ zIndex: "1200" }}
        >
          <Modal.Header closeButton style={{ backgroundColor: "transparent", color: "#777" }}>
            <Modal.Title>Finding Details</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: "transparent", borderRadius: "10px" }}>
            <div
              style={{
                fontFamily: "Arial, sans-serif",
                color: "#333",
              }}
            >
              {/* Specific Data Display in Styled Cards */}
              <div className="container">
                <div className="row g-3">
                  {[
                    { label: "Resource Name", key: "resourceName", bgColor: "rgba(255, 235, 238, 0.6)" },
                    { label: "Service", key: "service", bgColor: "rgba(227, 242, 253, 0.6)" },
                    { label: "Severity", key: "severity", bgColor: "rgba(232, 245, 233, 0.6)" },
                    { label: "Description", key: "description", bgColor: "rgba(255, 253, 231, 0.6)" },
                    { label: "Remediation", key: "remediation", bgColor: "rgba(243, 229, 245, 0.6)" },
                  ].map((item, index) => (
                    <div className="col-md-6 col-lg-4" key={index}>
                      <div
                        className="card shadow border-0"
                        style={{
                          borderRadius: "8px",
                          height: "200px",
                          overflow: "scroll",
                          scrollbarWidth: "none",
                          border: "1px solid #ddd",
                          backgroundColor: item.bgColor, // Unique background color for each card
                          position: "relative",
                        }}
                      >
                        <div
                          className="card-body"
                          style={{
                            borderRadius: "8px",
                            padding: "15px",
                            backgroundColor: "transparent", // Fully transparent body
                          }}
                        >
                          {item.key === "resourceName" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip style={{zIndex:"1500"}}>Copy to Clipboard</Tooltip>}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "5px",
                                right: "10px",
                                textAlign: "center",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleCopyToClipboard(
                                  selectedFinding.rawData?.[item.key]?.S ||
                                  selectedFinding[item.key]?.S ||
                                  "N/A"
                                )
                              }
                            >
                              {copiedResourceName ===
                              (selectedFinding.rawData?.[item.key]?.S || selectedFinding[item.key]?.S || "N/A") ? (
                                <>
                                  <FaCheck color="green" />
                                  <small style={{ color: "#999", display: "block", fontSize: "0.6rem" }}>
                                    Copied
                                  </small>
                                </>
                              ) : (
                                <>
                                  <FaClipboard color="#555" />
                                  <small style={{ color: "#999", display: "block", fontSize: "0.6rem" }}>
                                    Copy
                                  </small>
                                </>
                              )}
                          </div>

                          </OverlayTrigger>
                        )}
                          <h6
                            className="card-title fw-bold text-uppercase"
                            style={{
                              color: "#555",
                              fontSize: "1rem",
                              marginBottom: "10px",
                              letterSpacing: "1.5px",
                            }}
                          >
                            {item.label}
                          </h6>
                          <p
                            className="card-text"
                            style={{
                              fontSize: "0.85rem",
                              color: "#333",
                              letterSpacing: "1.5px",
                              wordBreak: "break-word",
                              lineHeight: "1.4",
                            }}
                          >
                            {typeof selectedFinding.rawData?.[item.key]?.S === "object"
                              ? JSON.stringify(selectedFinding.rawData?.[item.key]?.S, null, 2)
                              : selectedFinding.rawData?.[item.key]?.S ||
                                selectedFinding[item.key]?.S ||
                                "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* References Section */}
              {selectedFinding.references?.S && (() => {
                  try {
                    const references = JSON.parse(selectedFinding.references.S);
                    return references && references.length > 0 ? (
                      <div style={{ marginTop: "30px", textAlign: "center" }}>
                        <ul style={{ listStyleType: "none", padding: 0 }}>
                          {references.map((link, idx) => (
                            <li key={idx} style={{ marginBottom: "10px" }}>
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#6D5BFF",
                                  fontWeight: "bold",
                                  textDecoration: "none",
                                  wordBreak:"break-all",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null;
                  } catch (error) {
                    console.error("Error parsing references:", error);
                    return null;
                  }
                })()}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ justifyContent: "center", padding: "15px", backgroundColor: "transparent", border: "none" }}>
            <Button
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              style={{
                backgroundColor: "#6D5BFF",
                border: "none",
                fontWeight: "bold",
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default FindingsList;
